import React, {useMemo, useState, useEffect} from 'react'
import TpaTracker from './tracker'
import WelcomeModal from './welcomeModal'
import BalanceModal from './balanceModal'
import {Dialog} from '@/components/ui/dialog'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {cn} from '@/lib/utils'
import {useCustomQuery} from '@salesforce/commerce-sdk-react'
import {useCustomerType} from '@salesforce/commerce-sdk-react'
import {useTpaBalanceConfig} from 'hec/context/tpa-config'
import {useCurrentBasket} from '@/hooks/basket/useCurrentBasket'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCookieTracker} from '~/app/utils/cookieTracking/CookieTrackerProvider'
import {BuilderImage} from 'hec/types/common'

interface TpaBalanceProps extends React.HTMLAttributes<HTMLDivElement> {
    balancePopupText: string
    useMockData?: boolean
    mockData?: any
    isEdit: boolean
    isPreview: boolean
    className?: string
    bdLogo?: BuilderImage
    welcomeLogo?: BuilderImage
}

const TpaBalance: React.FC<TpaBalanceProps> = ({
    balancePopupText = '',
    useMockData,
    mockData,
    isEdit,
    isPreview,
    bdLogo,
    welcomeLogo,
    className,
    ...rest
}) => {
    const {site} = useMultiSite()
    const siteId = site.id
    const {currency: activeCurrency} = useCurrency()
    const {isRegistered} = useCustomerType()
    const shouldQuerySitePreferences = siteId === 'FSASTORE'

    // Get TPA balance configuration from context (computed server-side)
    const {balanceTrackerExpiryDays} = useTpaBalanceConfig()
    const {cookieValues} = useCookieTracker()
    const asiFlexAvailableBalance = cookieValues.asiFlexData?.asiFlexAvailableBalance ?? null
    const asiFlexParticipantBalanceConsented =
        cookieValues.asiFlexData?.asiFlexParticipantBalanceConsented ?? false
    // Use the server-side computed value directly
    const finalBalanceTrackerExpiryDays = useMemo(() => {
        // When using mock data, default to 60 days for testing expiry warning
        if (useMockData) {
            return 60
        }
        // Only use the server-side value if we should be querying site preferences
        if (shouldQuerySitePreferences) {
            return balanceTrackerExpiryDays
        }
        // For non-FSASTORE sites, return null (original behavior)
        return null
    }, [balanceTrackerExpiryDays, shouldQuerySitePreferences, useMockData])

    const {data: basket} = useCurrentBasket({enabled: true})
    const basketRevision = basket?.lastModified

    // Only query API if we don't already have asiFlexData from cookie
    const shouldQueryBalanceData =
        (isRegistered || (isEdit && !useMockData)) &&
        (asiFlexAvailableBalance === null || asiFlexAvailableBalance === undefined)

    // Controlled dialog state for Balance Modal - must be before early returns
    const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false)

    const {
        data: balanceData,
        isLoading,
        isError,
        error,
        refetch: refetchBalance
    } = useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    apiName: 'customers',
                    endpointPath: 'balanceDisplayData'
                }
            },
            rawResponse: false
        },
        {enabled: shouldQueryBalanceData}
    )

    useEffect(() => {
        if (basketRevision && shouldQueryBalanceData) void refetchBalance()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [basketRevision, shouldQueryBalanceData])

    let trackerBalanceData

    if (useMockData && (isEdit || isPreview)) {
        trackerBalanceData = {
            ...mockData
        }
    } else if (isRegistered && !isEdit && (balanceData as Record<string, unknown>)?.balance) {
        trackerBalanceData = {
            ...(balanceData as Record<string, unknown>)
        }
    } else if (asiFlexAvailableBalance !== null && asiFlexAvailableBalance !== undefined) {
        trackerBalanceData = {
            balance: {
                tracker: {
                    currentAvailableBalance: asiFlexAvailableBalance.toString(),
                    balanceUsagePercentage: '0',
                    usedSessionBalance: '0',
                    leftoverOrderAmount: '0'
                },
                popup: [
                    {
                        planType: 'ASIFLEX',
                        balance: asiFlexAvailableBalance.toString(),
                        deadlineDate: null,
                        message: '',
                        accountName: 'ASI',
                        expiryDate: null
                    }
                ]
            }
        }
    }

    if ((isLoading && shouldQueryBalanceData) || !trackerBalanceData) {
        return null
    } else if (isError && shouldQueryBalanceData) {
        console.error('Failed to get the TPA Balance data:', error)
        return null
    }

    const balancePopupData = trackerBalanceData.balance?.popup

    const showWelcomeModal = asiFlexAvailableBalance === null
    const showDetailsButton = showWelcomeModal

    const {
        message: welcomePopupContent = '',
        // This is temporarily ignored until the backend supports CMS functionality
        // show: showWelcomePopup = false,
        link: welcomeModalLink,
        linkText: welcomeModalLinkText,
        marketingConsentCheckbox
    } = trackerBalanceData?.popup || {}

    const handleDetailsClick = () => {
        setIsBalanceModalOpen(true)
    }

    return (
        <div {...rest} className={cn('p-2', className)}>
            <Dialog
                open={isBalanceModalOpen}
                onOpenChange={setIsBalanceModalOpen}
                data-testid="tpaBalance-Dialog"
            >
                <div className={cn('w-full', className)}>
                    <TpaTracker
                        trackerData={trackerBalanceData?.balance?.tracker}
                        balanceTrackerExpiryDays={finalBalanceTrackerExpiryDays || 60}
                        activeCurrency={activeCurrency}
                        popupData={balancePopupData}
                        onDetailsClick={handleDetailsClick}
                        showDetailsButton={showDetailsButton}
                        asiFlexAvailableBalance={asiFlexAvailableBalance}
                        asiFlexParticipantBalanceConsented={asiFlexParticipantBalanceConsented}
                        data-testid="tpaBalance-TpaTracker"
                    />
                </div>
                <BalanceModal
                    balancePopupData={balancePopupData}
                    balancePopupText={balancePopupText}
                    activeCurrency={activeCurrency}
                    logo={bdLogo}
                    data-testid="tpaBalance-BalanceModal"
                />
            </Dialog>
            <WelcomeModal
                marketingConsentCheckbox={marketingConsentCheckbox}
                popupContent={welcomePopupContent}
                show={showWelcomeModal}
                link={welcomeModalLink}
                linkText={welcomeModalLinkText}
                logo={welcomeLogo}
                isEdit={isEdit}
                data-testid="tpaBalance-WelcomeModal"
            />
        </div>
    )
}

export {TpaBalance}
